import { useHost } from '@/app/[domain]/host-provider';
import DataTable from '@/components/commons/data-table';
import { columnsCustomerOrder } from '@/components/sections/builder/elements/body/customer/customer_order/component/columns';
import { DetailDrawer } from '@/components/sections/builder/elements/body/customer/customer_order/component/detail-drawer';
import TableCustomerOrderMobile from '@/components/sections/builder/elements/body/customer/customer_order/component/table-mobile';
import { CustomerOrderElementDataType } from '@/components/sections/builder/elements/body/customer/customer_order/type';
import ModalAlert from '@/components/sections/modal-alert';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/use-toast';
import { dummyCustomerOrders } from '@/constant/dummyData/dummy-customer-order';
import { useCustomerListOrder } from '@/hooks/api/storefront/customer';
import { QueryParams } from '@/hooks/use-pagination';
import useModal from '@/hooks/useModal';
import { sendNotification } from '@/lib/actions/checkout/action';
import { usePickupInStore } from '@/stores/pickup-in-store';
import Cookies from 'js-cookie';
import React from 'react';
import { shallow } from 'zustand/shallow';

const getStatusTabOrder = (tab: string) => {
  let status;
  switch (tab) {
    case 'all':
      status = '';
      break;
    case 'unpaid':
      status = 'unpaid';
      break;
    case 'processing':
      status = 'paid,ready_to_process,processing,shipping,shipped,not_yet_shipped';
      break;
    case 'done':
      status = 'done';
      break;
    case 'cancel':
      status = 'cancel,cancelled,expired';
      break;
    default:
      status = '';
  }
  return status;
};

type TableOrderCustomerProps = {
  content: CustomerOrderElementDataType;
  preview: boolean;
  params?: QueryParams;
  setParams?: (pagination: QueryParams) => void;
};

export function TableCustomerOrder({
  content,
  preview,
  params,
  setParams,
}: TableOrderCustomerProps) {
  const token = Cookies.get('accessToken');
  const host = useHost();
  const detailDrawer = useModal();
  const modalNotify = useModal<{ orderId?: string }>();

  const { getStateByOrder, setLastClickedAt, setLoading } = usePickupInStore(
    (state) => ({
      getStateByOrder: state.getStateByOrder,
      setLastClickedAt: state.setLastClickedAt,
      setLoading: state.setLoading,
    }),
    shallow
  );
  const { lastClickedAt, isLoading: isLoadingNotify } = getStateByOrder(
    modalNotify.data?.orderId || ''
  );

  const queryParams = {
    page: params?.pageIndex === 0 ? 1 : params?.pageIndex,
    pageSize: params?.pageSize || 10,
    q: params?.q as string,
    status: getStatusTabOrder(params?.tab as string),
    startDate: params?.startDate,
    endDate: params?.endDate,
  };

  const { data, totalCount, isLoading } = useCustomerListOrder(token as string, queryParams);

  const tableData = React.useMemo(() => {
    if (isLoading) {
      return Array(4).fill({});
    }
    return preview ? dummyCustomerOrders : data || [];
  }, [isLoading, preview, data]);

  const columns = React.useMemo(
    () =>
      isLoading
        ? columnsCustomerOrder(detailDrawer, content).map((column) => ({
            ...column,
            cell: () => <Skeleton className='h-4 w-full' />,
          }))
        : columnsCustomerOrder(detailDrawer, content),
    [isLoading]
  );

  const handleClickNotify = async () => {
    if (isLoadingNotify) return;
    setLoading(modalNotify.data?.orderId || '', true);

    try {
      const res = await sendNotification(host as string, modalNotify.data?.orderId || '');
      const now = Date.now();

      if (!lastClickedAt || now - lastClickedAt >= 60_000) {
        setLastClickedAt(modalNotify.data?.orderId || '', now);
      }

      toast({
        title: res?.success ? 'Notification sent!' : 'Error',
        description: res?.success
          ? 'Store has been notified that you are on your way.'
          : res?.message || 'Failed to notify the store.',
        duration: 5000,
        variant: res?.success ? 'success' : 'destructive',
      });
    } finally {
      setLoading(modalNotify.data?.orderId || '', false);
    }
  };

  return (
    <>
      <div className='hidden md:block'>
        <DataTable
          data={tableData || []}
          isLoading={isLoading}
          columns={columns}
          totalCount={totalCount || 0}
          pagination={{
            pageIndex: (params?.pageIndex as number) - 1,
            pageSize: params?.pageSize as number,
          }}
          onPageChange={(page) => {
            setParams?.({ ...params, pageIndex: page, pageSize: params?.pageSize || 10 });
          }}
          onPageSizeChange={(size) => {
            setParams?.({ ...params, pageSize: size, pageIndex: 1 });
          }}
        />
      </div>
      <div className='block md:hidden'>
        <TableCustomerOrderMobile
          data={tableData || []}
          isLoading={isLoading}
          totalCount={totalCount || 0}
          pagination={{
            pageIndex: (params?.pageIndex as number) - 1,
            pageSize: params?.pageSize as number,
          }}
          onPageChange={(page) => {
            setParams?.({ ...params, pageIndex: page as number, pageSize: params?.pageSize || 10 });
          }}
          onPageSizeChange={(size) => {
            setParams?.({ ...params, pageSize: size, pageIndex: 1 });
          }}
          drawer={detailDrawer}
        />
      </div>

      <DetailDrawer
        openModal={detailDrawer.isShow}
        setOpenModal={detailDrawer.setIsShow}
        data={detailDrawer.data as { order_id: string }}
        token={token}
        content={content}
        modalNotify={modalNotify as ReturnType<typeof useModal>}
      />

      <ModalAlert
        openModal={modalNotify.isShow}
        setOpenModal={modalNotify.setIsShow}
        title={`Let the store know you're nearby`}
        description={`We'll inform the store that you're on the way. Your order will be prepared shortly.`}
        buttonConfirmText='Yes, Notify Store'
        buttonCancelText='Cancel'
        onConfirm={handleClickNotify}
        onCancel={() => {
          modalNotify.setIsShow(false);
        }}
        colorScheme={content?.colorScheme}
      />
    </>
  );
}
